import React from 'react';
import { useDispatch } from 'react-redux';
import { Nav, Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppDispatch } from '../../store';
import { setActiveView } from '../../store/slices/dashboardSlice';
import { useAuth } from '../../hooks/useAuth';
import { useGetPayrollUsersUserIdConfig } from '../../api/generated/arrayscapeGamingDashboardAPI';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasAnyRole, timesheetReports } = useAuth();
  const { data: timesheetConfig } = useGetPayrollUsersUserIdConfig(user?.id || '', {
    query: { enabled: !!user?.id, refetchOnWindowFocus: true, staleTime: 0 },
  });
  const timesheetsVisible = user?.role === 'admin' || timesheetConfig?.timesheets_enabled === true || timesheetReports.length > 0;

  const handleViewChange = (view: any, path: string) => {
    dispatch(setActiveView(view));
    navigate(path);
  };

  const menuItems = [
    {
      key: 'commissions',
      icon: 'bi-currency-dollar',
      label: 'Commissions',
      path: '/commissions',
      pathPrefix: '/commissions',
      roles: ['admin', 'finance', 'vp_sales', 'sales_manager', 'salesperson'],
    },
    {
      key: 'applicants',
      icon: 'bi-file-earmark-person',
      label: 'Applicants',
      path: '/applicants',
      pathPrefix: '/applicants',
      roles: ['admin', 'events_manager'],
    },
    {
      key: 'customers',
      icon: 'bi-building',
      label: 'Customers',
      path: '/customers',
      roles: ['salesperson', 'sales_manager', 'vp_sales', 'admin', 'events_manager', 'finance', 'marketing'],
    },
    {
      key: 'email-marketing',
      icon: 'bi-envelope-fill',
      label: 'Email Marketing',
      path: '/email-marketing/contacts',
      pathPrefix: '/email-marketing',
      roles: ['salesperson', 'sales_manager', 'vp_sales', 'admin', 'events_manager', 'marketing'],
    },
    {
      key: 'financial-reports',
      icon: 'bi-graph-up',
      label: 'Financial Reports',
      path: '/financial-reports/financials',
      roles: ['admin', 'vp_sales'],
    },
    {
      key: 'form-submissions',
      icon: 'bi-inbox',
      label: 'Form Submissions',
      path: '/form-submissions',
      pathPrefix: '/form-submissions',
      roles: ['admin', 'marketing', 'salesperson', 'sales_manager', 'vp_sales', 'events_manager'],
    },
    {
      key: 'invoices',
      icon: 'bi-receipt',
      label: 'Invoices',
      path: '/invoices',
      roles: ['admin', 'salesperson', 'finance', 'sales_manager', 'vp_sales'],
    },
    {
      key: 'invoice-payments',
      icon: 'bi-bank',
      label: 'Invoice Payments',
      path: '/invoice-payments',
      pathPrefix: '/invoice-payments',
      roles: ['admin', 'salesperson', 'finance', 'sales_manager', 'vp_sales'],
    },
    {
      key: 'schedule',
      icon: 'bi-calendar-check',
      label: 'Schedule',
      path: '/schedule',
      roles: ['salesperson', 'sales_manager', 'vp_sales', 'admin', 'events_manager', 'marketing'],
    },
    {
      key: 'sling-locations',
      icon: 'bi-geo-alt',
      label: 'Sling Locations',
      path: '/sling-locations',
      roles: ['sales_manager', 'vp_sales', 'admin', 'events_manager', 'marketing'],
    },
    {
      key: 'team',
      icon: 'bi-people',
      label: 'Team Dashboard',
      path: '/dashboard/team',
      roles: ['admin'],
    },
    {
      key: 'timesheets',
      icon: 'bi-clock',
      label: 'Timesheets',
      path: '/timesheets',
      pathPrefix: '/timesheets',
      hidden: !timesheetsVisible,
    },
  ];

  const adminMenuItems = [
    {
      key: 'bank-transactions',
      icon: 'bi-bank2',
      label: 'Bank Transactions',
      path: '/admin/bank-transactions',
      pathPrefix: '/admin/bank-transactions',
      roles: ['admin', 'finance'],
    },
    {
      key: 'payroll',
      icon: 'bi-clock-history',
      label: 'Payroll',
      path: '/admin/payroll/activities',
      pathPrefix: '/admin/payroll',
      roles: ['admin'],
    },
    {
      key: 'items',
      icon: 'bi-box-seam',
      label: 'Products & Services',
      path: '/admin/items',
      pathPrefix: '/admin/items',
      roles: ['admin'],
    },
    {
      key: 'sync-history',
      icon: 'bi-arrow-repeat',
      label: 'Sync History',
      path: '/admin/sync-history',
      pathPrefix: '/admin/sync-history',
      roles: ['admin'],
    },
    {
      key: 'commissions-audit',
      icon: 'bi-clipboard2-data',
      label: 'Commissions Audit',
      path: '/commissions/audit',
      pathPrefix: '/commissions/audit',
      roles: ['admin', 'vp_sales'],
    },
    {
      key: 'settings',
      icon: 'bi-gear',
      label: 'System Settings',
      path: '/admin/settings/general',
      pathPrefix: '/admin/settings',
      roles: ['admin'],
    },
    {
      key: 'users',
      icon: 'bi-people-fill',
      label: 'User Management',
      path: '/admin/users',
      pathPrefix: '/admin/users',
      roles: ['admin'],
    },
  ];

  const filteredMenuItems = menuItems.filter(item =>
    !item.hidden && (!item.roles || hasAnyRole(item.roles))
  );

  const filteredAdminItems = adminMenuItems.filter(item => 
    !item.roles || hasAnyRole(item.roles)
  );

  const sidebarWidth = isCollapsed ? '70px' : '280px';

  return (
    <div
      className="bg-body-tertiary border-end"
      style={{
        width: sidebarWidth,
        height: 'calc(100vh - 70px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'sticky',
        top: '70px',
        left: 0,
        zIndex: 1040,
        transition: 'width 0.3s ease'
      }}
    >
      <div className="p-3">
        {/* Toggle Button */}
        <div className="mb-3 d-flex justify-content-end">
          <Button
            variant="link"
            onClick={onToggle}
            className="p-0 text-body"
            style={{ border: 'none', background: 'none' }}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <i className={`bi ${isCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'} fs-5`}></i>
          </Button>
        </div>

        <Nav className="flex-column">
          {filteredMenuItems.map((item) => (
            <Nav.Link
              key={item.key}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleViewChange(item.key, item.path);
              }}
              className={`mb-2 rounded px-3 py-2 d-flex align-items-center text-decoration-none ${
                item.pathPrefix ? location.pathname.startsWith(item.pathPrefix) : location.pathname === item.path
                  ? 'bg-primary text-white'
                  : 'text-body hover-bg-light'
              }`}
              style={{
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              title={isCollapsed ? item.label : ''}
            >
              <i className={`${item.icon} ${isCollapsed ? '' : 'me-3'} fs-5`}></i>
              {!isCollapsed && <span className="fw-medium">{item.label}</span>}
            </Nav.Link>
          ))}

          {filteredAdminItems.length > 0 && (
            <>
              <hr className="my-3" />
              {!isCollapsed && (
                <div className="px-3 mb-2">
                  <small className="text-muted text-uppercase fw-semibold">
                    Administration
                  </small>
                </div>
              )}
              {filteredAdminItems.map((item) => (
                <Nav.Link
                  key={item.key}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleViewChange(item.key, item.path);
                  }}
                  className={`mb-2 rounded px-3 py-2 d-flex align-items-center text-decoration-none ${
                    location.pathname.startsWith(item.pathPrefix)
                      ? 'bg-danger text-white'
                      : 'text-body hover-bg-light'
                  }`}
                  style={{
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                  title={isCollapsed ? item.label : ''}
                >
                  <i className={`${item.icon} ${isCollapsed ? '' : 'me-3'} fs-5`}></i>
                  {!isCollapsed && <span className="fw-medium">{item.label}</span>}
                </Nav.Link>
              ))}
            </>
          )}
        </Nav>

      </div>
    </div>
  );
};

export default Sidebar;